import * as XLSX from 'xlsx';

/**
 * Единая точка чтения книги (.csv / .tsv / .xlsx / .xls / Google Sheets)
 * для всех импорт-модалок. Централизует две тонкости, которые иначе
 * дублируются (и уже расходились между модалками):
 *
 *   1. Кодировка CSV. SheetJS при `type: 'array'` декодит байты как
 *      Latin-1 → кириллица превращается в `Ð¡ÐºÐ¸Ð»Ð»`. Поэтому текстовые
 *      форматы читаем UTF-8-строкой (`type: 'string'`), а бинарные
 *      XLSX/XLS (zip-архив) — как массив байт.
 *   2. Различаем форматы по magic-bytes (`PK` = 0x50 0x4B), а не по имени
 *      файла: Google после `export?format=csv` отдаёт файл без расширения.
 */
export async function readWorkbook(file: File): Promise<XLSX.WorkBook> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const looksZip = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
  return looksZip
    ? XLSX.read(buf, { type: 'array' })
    : XLSX.read(new TextDecoder('utf-8').decode(bytes), { type: 'string' });
}

/**
 * Выбрать имя листа: переданное (из UI-селектора вкладок), если оно есть
 * в книге, иначе первый лист. Возвращает `null`, если книга пуста.
 */
export function resolveSheetName(wb: XLSX.WorkBook, requested?: string): string | null {
  if (requested && wb.SheetNames.includes(requested)) return requested;
  return wb.SheetNames[0] ?? null;
}

/**
 * Прочитать лист в `{ headers, rows }`: первая непустая строка — заголовки,
 * дальше строки данных с отфильтрованными полностью пустыми. Используется
 * модалками справочников и пользователей (грид-импорт в SpacePage работает
 * с CSV-текстом и парсит отдельно).
 */
export function sheetToHeadersRows(
  wb: XLSX.WorkBook,
  sheetName: string,
): { headers: string[]; rows: string[][] } {
  const sheet = wb.Sheets[sheetName];
  if (!sheet) return { headers: [], rows: [] };
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
    defval: '',
    raw: false,
  });
  if (aoa.length === 0) return { headers: [], rows: [] };
  const headers = (aoa[0] as unknown[]).map((v) => String(v ?? '').trim());
  const rows = aoa
    .slice(1)
    .map((r) => {
      const arr = r as unknown[];
      return headers.map((_, i) => String(arr[i] ?? '').trim());
    })
    .filter((r) => r.some((cell) => cell !== ''));
  return { headers, rows };
}

/**
 * Привести заголовок колонки к виду, по которому его можно сравнивать с
 * названием поля/колонки при автоматическом маппинге.
 *
 * Заголовки из Excel и Google Sheets внешне совпадают с названием поля,
 * но байт-в-байт — нет: неразрывные пробелы (частый гость после копипаста
 * из Confluence), двойные пробелы, перенос строки внутри ячейки, кавычки
 * из CSV, другой регистр, «ё» против «е». Без нормализации такие колонки
 * молча оставались несопоставленными, хотя на экране выглядели одинаково.
 */
export function normalizeHeader(raw: string): string {
  return raw
    .replace(/[\u00a0\u2007\u202f\s]+/g, ' ')
    .replace(/^["']+|["']+$/g, '')
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е');
}

/**
 * Имя без пояснения в скобках и хвостовой пунктуации: заголовок
 * «Оценка важности (1 высокий, 2 средний, 3 низкий)» описывает то же
 * поле, что и «Оценка важности», а «Логин*» — то же, что «Логин».
 */
function headerBase(raw: string): string {
  return normalizeHeader(raw.replace(/[([{][^)\]}]*[)\]}]/g, ' '))
    .replace(/[\s:*.,;-]+$/, '')
    .trim();
}

/**
 * Сопоставить заголовки файла с полями-получателями. Два прохода:
 * сначала раздаются точные совпадения по нормализованному имени, и только
 * потом — совпадения «без скобочного пояснения». Порядок важен: иначе
 * колонка с пояснением могла бы занять поле, которое дальше по файлу
 * названо точно.
 *
 * Одно поле достаётся одной колонке — вторая остаётся несопоставленной,
 * чтобы значения не затирали друг друга молча.
 *
 * @param keysOf — все имена, под которыми поле может встретиться в файле
 *   (например, `[slug, name]` или список синонимов).
 */
export function matchHeaders<T>(
  headers: string[],
  targets: readonly T[],
  keysOf: (target: T) => string[],
): Map<string, T> {
  const result = new Map<string, T>();
  const taken = new Set<T>();
  for (const norm of [normalizeHeader, headerBase]) {
    for (const header of headers) {
      if (result.has(header)) continue;
      const h = norm(header);
      if (!h) continue;
      const match = targets.find(
        (t) => !taken.has(t) && keysOf(t).some((key) => norm(key) === h),
      );
      if (match) {
        result.set(header, match);
        taken.add(match);
      }
    }
  }
  return result;
}
